<template>
    <div class="learning-admin-path-edit-step" data-test-view="learning-admin-path-edit-step">
        <div class="body">
            <div v-if="element.type === 'existing'">
                <l-input-text
                    v-model="query"
                    type="text"
                    :optional="true"
                    :label="``"
                    :placeholder="$t(`learning.admin.path.details.steps.search.placeholder`)"
                    data-test-id="learning-admin-path-edit-step-search"
                />
                <div v-if="list.length">
                    <l-paginate
                        :list="list"
                        :items-per-page="5"
                        :on-item-click="addElementToStep"
                        :no-data-text="$t(`learning.admin.element.empty`)"
                    >
                        <template #item="{ item, index }">
                            <l-list-item
                                class="clickable element-list-item"
                                data-test-class="learning-admin-path-edit-step-list-item"
                                :data-test-id="`learning-admin-path-edit-step-list-item-${index}`"
                            >
                                <template #title>
                                    <span class="element-name ellipsis">{{ item.name }}</span>
                                </template>
                                <template #subtitle>
                                    <div class="element-description ellipsis">
                                        <l-text :text="item.description" one-line />
                                    </div>
                                </template>
                                <template #left>
                                    <div class="element-list-item-icon">
                                        <img
                                            class="element-list-item-icon-img"
                                            :src="icons[item.type]"
                                            :alt="item.type"
                                        />
                                    </div>
                                </template>
                            </l-list-item>
                        </template>
                    </l-paginate>
                </div>
                <div v-else>
                    <div v-if="!list.length && query.length >= 2">
                        {{ $t(`learning.admin.path.details.steps.search.notFound`, { query }) }}
                        <hr />
                    </div>
                    <div v-else>
                        <br />
                    </div>
                    <l-learning-admin-element-create-select-type
                        @select="selectType"
                        @cancel="goBack"
                    />
                </div>
            </div>

            <div v-else>
                <!-- Error -->
                <div v-if="error" id="error">
                    <l-text-alert
                        type="danger"
                        :text="$t('learning.admin.element.details.error.edit')"
                    />
                </div>
                <!--/Error -->

                <l-learning-admin-element-edit
                    :element="element"
                    :disable-submit="disableSubmit"
                    :show-delete-btn="false"
                    :cancel="goBackToExisting"
                    :submit="createElement"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import ObjectId from "bson-objectid";

import LInputText from "@/components/LInputText";
import LListItem from "@/components/LListItem";
import LPaginate from "@/components/LPaginate";
import LText from "@/components/LText";
import LTextAlert from "@/components/LTextAlert";

import LLearningAdminElementCreateSelectType from "@/components/LLearningAdminElementCreateSelectType";
import LLearningAdminElementEdit from "@/components/LLearningAdminElementEdit";

import task from "@/img/icons/learning/task.png";
import quiz from "@/img/icons/learning/quiz.png";
import article from "@/img/icons/learning/article.png";
import video from "@/img/icons/learning/video.png";

export default {
    components: {
        LInputText,
        LListItem,
        LPaginate,
        LText,
        LTextAlert,

        LLearningAdminElementCreateSelectType,
        LLearningAdminElementEdit,
    },
    props: {
        pathId: {
            type: String,
            required: true,
        },
        addElementToStep: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            query: "",
            element: {
                type: "existing",
                name: "",
                description: "",
                location: {
                    type: "link",
                    url: "",
                },
                questions: [],
                open: {
                    enabled: false,
                    targeting: {
                        all: true,
                        localTeams: [],
                        users: [],
                    },
                },
            },
            list: [],
            disableSubmit: false,
            error: false,
        };
    },
    computed: {
        ...mapGetters({
            locale: "users/current/locale",
            key: "learning/storage/key",
            search: "learning/element/search",
        }),
    },
    watch: {
        query(newQuery) {
            if (newQuery.length) {
                this.list = this.search(newQuery, { useFilteredList: false });
            } else {
                this.list = [];
            }
        },
    },
    created() {
        this.icons = { quiz, article, video, task };
    },
    methods: {
        selectType(type) {
            this.element.type = type;

            // Initialize questions array if not present
            if (this.element.type === "quiz" && !Array.isArray(this.element.questions)) {
                this.element.questions = [];
            }
        },

        goBack() {
            this.$router.push(`/learning/admin/path/${this.pathId}/edit/steps`);
        },

        goBackToExisting() {
            this.element.type = "existing";
            this.query = "";
        },

        createElement(element) {
            this.element = element;

            this.error = false;
            this.disableSubmit = true;

            const id = ObjectId().toString();

            const data = {
                commonId: id,
                type: this.element.type,
                name: this.element.name,
                description: this.element.description,
                mimetype: this.element.mimetype,
                location: {
                    type: this.element.location.type,
                    url: this.element.location.url,
                    key: this.key, // From `learning/storage`
                },
                open: { ...this.element.open },
                taggedUsers: (this.element.taggedUsers || []).map(({ _id }) => _id),
                language: this.locale,
                version: 0,
                questions: this.element.questions,
                tags: [], // TODO
            };

            return this.$store
                .dispatch({
                    type: "learning/element/update",
                    payload: { element: data },
                })
                .then(() => {
                    this.addElementToStep(data);
                })
                .catch(() => {
                    this.error = true;

                    this.$nextTick(() => {
                        document.getElementById("error")?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                            inline: "nearest",
                        });
                    });
                })
                .finally(async () => {
                    // Clear storage key
                    await this.$store.dispatch({ type: "learning/storage/clear" });

                    this.disableSubmit = false;
                });
        },
    },
};
</script>

<style scoped>
.learning-admin-path-edit-step >>> .list-item-right {
    width: calc(100% - 110px);
}
.element-list-item-icon {
    width: 110px;
    text-align: center;
}

.element-list-item-icon-img {
    height: 80px;
    opacity: 0.8;
    transition: all 0.3s ease-in;
}
.element-list-item:hover .element-list-item-icon-img {
    transform: scale(1.05);
    opacity: 1;
}
</style>
